import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Settings, Search, AlertTriangle, CheckCircle, Clock, Wrench, User, Calendar } from 'lucide-react';

function PlantMachinery() {
  const { API } = useContext(AppContext);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMachinery();
  }, [statusFilter]);

  const fetchMachinery = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/machinery`, {
        params: { status: statusFilter },
        
      });
      setMachinery(response.data);
    } catch (error) {
      console.error('Error fetching machinery:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Running': return 'bg-green-100 text-green-700 border-green-200';
      case 'Shutdown': return 'bg-red-100 text-red-700 border-red-200';
      case 'Under Maintenance': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Scheduled Maintenance': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Awaiting Parts': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Running': return <CheckCircle size={16} className="text-green-500" />;
      case 'Shutdown': return <AlertTriangle size={16} className="text-red-500" />;
      case 'Under Maintenance': return <Wrench size={16} className="text-orange-500" />;
      case 'Scheduled Maintenance': return <Clock size={16} className="text-blue-500" />;
      case 'Awaiting Parts': return <Clock size={16} className="text-yellow-500" />;
      default: return null;
    }
  };

  const filteredMachinery = machinery.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: machinery.length,
    running: machinery.filter(m => m.status === 'Running').length,
    shutdown: machinery.filter(m => m.status === 'Shutdown').length,
    maintenance: machinery.filter(m => ['Under Maintenance', 'Scheduled Maintenance', 'Awaiting Parts'].includes(m.status)).length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading machinery data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="plant-machinery-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plant & Machinery</h1>
          <p className="text-gray-500 mt-1">Overview of all plant equipment and their status</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2">
            <Settings className="text-[#1a237e]" size={18} />
            <p className="text-sm text-gray-500">Total Machines</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Running</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.running}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="text-red-500" size={18} />
            <p className="text-sm text-red-600 font-medium">Shutdown</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.shutdown}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2">
            <Wrench className="text-orange-500" size={18} />
            <p className="text-sm text-orange-600 font-medium">Under Maintenance</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{stats.maintenance}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200">
          {['all', 'Running', 'Shutdown', 'Under Maintenance', 'Scheduled Maintenance', 'Awaiting Parts'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, type, or owner..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Machinery Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Machine ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Capacity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Last Maintenance</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Next Maintenance</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMachinery.map((machine) => (
                <tr key={machine.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{machine.id}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{machine.name}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{machine.type}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{machine.location}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-1.5">
                      <User size={14} className="text-gray-400" />
                      <span className="text-sm text-gray-600">{machine.owner}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{machine.capacity}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{machine.lastMaintenance}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{machine.nextMaintenance}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center space-x-1.5 w-fit ${getStatusColor(machine.status)}`}>
                      {getStatusIcon(machine.status)}
                      <span>{machine.status}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Remarks for machines with issues */}
      {machinery.filter(m => m.remarks && m.status !== 'Running').length > 0 && (
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <h3 className="text-sm font-semibold text-yellow-800 mb-3">Attention Required</h3>
          <div className="space-y-2">
            {machinery.filter(m => m.remarks && m.status !== 'Running').map((machine) => (
              <div key={machine.id} className="flex items-start space-x-3 text-sm">
                <span className="font-mono text-yellow-700">{machine.id}</span>
                <span className="text-yellow-800">{machine.name}: {machine.remarks}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlantMachinery;
